import { useEffect, useState } from 'react';
import { useSessionTracking } from 'hooks/useSessionTracking';
import AppSettings from 'config/AppSettings';
import getFeatureFlags from 'helpers/getFeatureFlags';
import clsx from 'clsx';
import { CookieConsent } from 'components/CookieConsent/CookieConsent';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import AppRouter from 'routers/AppRouter';
import MobileWarning from 'components/MobileWarning';

import 'normalize.css/normalize.css';
import './assets/styles/styles.scss';
import StorageHandler from 'config/StorageHandler';
import { storageKeys, language } from 'config/settings.json';
import { captureCampaignRegistrationCode } from 'helpers/campaignRegistrationCode';
import { ensureCurrentAppStorage } from 'helpers/ensureCurrentAppStorage';
import { getLangFile, normalizeLangParam, toLangPreference } from 'helpers/languageParam';
import locales from 'config/i18n';

import i18n from 'i18n-react';
import { AppContext } from './AppContext';
import { useSanity } from 'hooks/useSanity';

window.global ||= window;

import './instrument';

function isCookiesDisabled(hasMobileSearchParam, hasSourceSearchParam) {
  // Path prefixes on which cookie consent should never be shown, e.g. views embedded in a native webview.
  const cookieExemptPaths = ['/directory/listing/', '/register-mobile', '/quick-register-mobile'];
  const isExemptPath = cookieExemptPaths.some((path) => {
    return window.location.pathname.startsWith(path);
  });
  return isExemptPath || hasMobileSearchParam || hasSourceSearchParam;
}

function isMobileWarningEnabled(hasMobileSearchParam) {
  const mobileUaRegex = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i;
  const isMobileDevice = mobileUaRegex.test(navigator.userAgent);
  return (
    isMobileDevice &&
    !hasMobileSearchParam &&
    import.meta.env.VITE_ENABLE_PREREGISTRATION !== 'true' &&
    !import.meta.env.DEV
  );
}

export function App() {
  // FIXME: Only computed once on mount, not rechecked on navigations. If that's not acceptable, recheck it.
  // Would (probably) require an update to be contained within router to access path updates.
  const searchParams = new URLSearchParams(window.location.search);
  const hasMobileSearchParam = searchParams.get('mobile') === 'true';
  const hasSourceSearchParam = searchParams.get('source') === 'true';

  const [loading, setLoading] = useState(true);
  const [featureFlags, setFeatureFlags] = useState({});

  useSanity();

  useSessionTracking();

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);

    captureCampaignRegistrationCode(window.location.search);

    StorageHandler.exists(storageKeys.lang).then((languageExists) => {
      if (languageExists) {
        if (params.get('lang')) {
          const normalizedCode = getLangFile(params.get('lang'));
          const match = normalizeLangParam(normalizedCode, language, locales);
          if (!match) {
            i18n.setTexts(locales.en);
          } else {
            StorageHandler.save(storageKeys.lang, toLangPreference(match));
            i18n.setTexts(locales[match.localeKey] ?? locales.en);
          }
        } else {
          StorageHandler.load(storageKeys.lang).then((lang) => {
            i18n.setTexts(locales[lang?.langFile] ?? locales.en);
          });
        }
      } else if (params.get('lang')) {
        const normalizedCode = getLangFile(params.get('lang'));
        const match = normalizeLangParam(normalizedCode, language, locales);
        if (!match) {
          i18n.setTexts(locales.en);
        } else {
          StorageHandler.save(storageKeys.lang, toLangPreference(match));
          window.location.reload();
        }
      } else {
        StorageHandler.save(storageKeys.lang, {
          country: 'English',
          code: 'en',
          langFile: 'en',
        });
        i18n.setTexts(locales.en);

        window.location.reload();
      }
    });
  }, []);

  useEffect(() => {
    ensureCurrentAppStorage()
      .then(() => {
        return AppSettings();
      })
      .then(async (res) => {
        if (res) {
          const lang = await StorageHandler.load(storageKeys.lang);
          const flags = await getFeatureFlags();
          i18n.setTexts(locales[lang?.langFile] ?? locales.en);
          setFeatureFlags(flags);
          setLoading(false);
        }
      });
  }, []);

  // const setAsked = () => {
  //   // this.setState({ askedBefore: true });
  //   // StorageHandler.save('askedMobile', true);
  //   window.history.back();
  // };

  return (
    <div
      className={clsx('container__router', {
        'featureFlagStyles__headers--bold': featureFlags.titles_to_bold,
        'featureFlagStyles__headers--capitalize': featureFlags.titles_to_capitalize,
        'featureFlagStyles__headers--uppercase': featureFlags.titles_to_uppercase && !featureFlags.titles_to_capitalize,
        [`clientStyles__custom--${import.meta.env.VITE_APP_ID}`]: import.meta.env.VITE_APP_ID,
      })}
    >
      {loading && (
        <div className="appLoader">
          <FontAwesomeIcon icon={['fas', 'circle-notch']} spin />
        </div>
      )}
      {!loading && (
        <AppContext>
          <AppRouter />
          <MobileWarning disabled={!isMobileWarningEnabled(hasMobileSearchParam)} />
          <CookieConsent disabled={isCookiesDisabled(hasMobileSearchParam, hasSourceSearchParam)} />
        </AppContext>
      )}
    </div>
  );
}
